/**
 * Composite a shared screen with an optional speaker camera
 * floating at the bottom center. The canvas stream is what gets recorded.
 */

import { createCanvasCaptureStream } from '@/lib/pitch/canvasCaptureStream';
import { startFrameLoop } from '@/lib/pitch/frameLoop';
import {
  PITCH_RECORD_HEIGHT,
  PITCH_RECORD_WIDTH,
  pitchComposeSizeFromCapture,
} from '@/lib/pitch/pitchRecordQuality';
import { createSpeakerFramePump } from '@/lib/pitch/speakerFramePump';

const DEFAULT_WIDTH = PITCH_RECORD_WIDTH;
const DEFAULT_HEIGHT = PITCH_RECORD_HEIGHT;

export function speakerBubbleLayout(cw, ch) {
  const width = Math.max(1, Number(cw) || DEFAULT_WIDTH);
  const height = Math.max(1, Number(ch) || DEFAULT_HEIGHT);
  const w = Math.round(width * 0.22);
  const h = Math.round(w * (9 / 16));
  const margin = Math.round(height * 0.045);
  return {
    x: Math.round((width - w) / 2),
    y: Math.max(0, Math.round(height - h - margin)),
    w,
    h,
    radius: Math.max(8, Math.round(Math.min(w, h) * 0.12)),
  };
}

function drawContain(ctx, source, cw, ch) {
  const iw = source.videoWidth || source.width;
  const ih = source.videoHeight || source.height;
  if (!iw || !ih) return;
  const ir = iw / ih;
  const cr = cw / ch;
  let dw;
  let dh;
  let dx;
  let dy;
  if (ir > cr) {
    dw = cw;
    dh = cw / ir;
    dx = 0;
    dy = (ch - dh) / 2;
  } else {
    dh = ch;
    dw = ch * ir;
    dx = (cw - dw) / 2;
    dy = 0;
  }
  ctx.drawImage(source, dx, dy, dw, dh);
}

function drawCoverLocal(ctx, source, w, h) {
  const iw = source.videoWidth || source.width;
  const ih = source.videoHeight || source.height;
  if (!iw || !ih) return;
  const ir = iw / ih;
  const cr = w / h;
  let dw;
  let dh;
  let dx;
  let dy;
  if (ir > cr) {
    dh = h;
    dw = h * ir;
    dx = (w - dw) / 2;
    dy = 0;
  } else {
    dw = w;
    dh = w / ir;
    dx = 0;
    dy = (h - dh) / 2;
  }
  ctx.drawImage(source, dx, dy, dw, dh);
}

function roundedRectPath(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

async function waitForVideoReady(video) {
  if (video.readyState >= 2 && video.videoWidth) return;
  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      if (video.videoWidth) resolve();
      else reject(new Error('Shared screen video failed to load'));
    }, 4000);
    const onReady = () => {
      if (!video.videoWidth && video.readyState < 2) return;
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('Shared screen video failed to load'));
    };
    const cleanup = () => {
      clearTimeout(timer);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('error', onError);
    };
    video.addEventListener('loadeddata', onReady);
    video.addEventListener('loadedmetadata', onReady);
    video.addEventListener('error', onError);
  });
}

function attachPreviewVideo(stream) {
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.srcObject = stream;
  return video;
}

/**
 * @returns {Promise<{ stream: MediaStream, stop: () => void }>}
 */
export async function createPitchScreenCompositor({
  screenStream,
  speakerStream = null,
  width = null,
  height = null,
} = {}) {
  if (!screenStream) {
    throw new Error('screenStream is required');
  }

  const screenVideo = attachPreviewVideo(screenStream);
  await screenVideo.play();
  await waitForVideoReady(screenVideo);

  const sized = (width != null && height != null)
    ? { width: Math.max(2, Math.round(width)), height: Math.max(2, Math.round(height)) }
    : pitchComposeSizeFromCapture(screenVideo.videoWidth, screenVideo.videoHeight);
  const composeWidth = sized.width;
  const composeHeight = sized.height;

  const canvas = document.createElement('canvas');
  canvas.width = composeWidth;
  canvas.height = composeHeight;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }

  let speakerVideo = null;
  if (speakerStream) {
    speakerVideo = attachPreviewVideo(speakerStream);
    try {
      await speakerVideo.play();
      await waitForVideoReady(speakerVideo);
    } catch (err) {
      console.warn('Pitch speaker camera preview failed:', err);
      speakerVideo.srcObject = null;
      speakerVideo = null;
    }
  }

  let stopped = false;
  const bubble = speakerBubbleLayout(composeWidth, composeHeight);
  let capture = null;
  let speakerPump = null;

  const ensurePlaying = (el) => {
    if (!el || stopped) return;
    if (el.paused || el.ended) {
      el.play().catch(() => {});
    }
  };

  if (speakerVideo) {
    const track = speakerStream?.getVideoTracks?.()?.[0] || null;
    speakerPump = createSpeakerFramePump(track, speakerVideo);
  }

  const draw = () => {
    if (stopped) return;
    ensurePlaying(screenVideo);
    ensurePlaying(speakerVideo);
    speakerPump?.kick?.();
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, composeWidth, composeHeight);
    if (screenVideo.readyState >= 2 && (screenVideo.videoWidth || 0) > 0) {
      drawContain(ctx, screenVideo, composeWidth, composeHeight);
    }
    const speakerSource = speakerPump?.getSource?.() || (
      speakerVideo && speakerVideo.readyState >= 2 ? speakerVideo : null
    );
    if (speakerSource) {
      ctx.save();
      roundedRectPath(ctx, bubble.x, bubble.y, bubble.w, bubble.h, bubble.radius);
      ctx.clip();
      ctx.translate(bubble.x + bubble.w / 2, bubble.y + bubble.h / 2);
      ctx.scale(-1, 1);
      ctx.translate(-bubble.w / 2, -bubble.h / 2);
      drawCoverLocal(ctx, speakerSource, bubble.w, bubble.h);
      ctx.restore();

      ctx.save();
      roundedRectPath(ctx, bubble.x, bubble.y, bubble.w, bubble.h, bubble.radius);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = Math.max(2, Math.round(composeWidth / 420));
      ctx.stroke();
      ctx.restore();
    }
    capture?.pushFrame?.();
  };

  try {
    capture = createCanvasCaptureStream(canvas);
  } catch (err) {
    speakerPump?.stop?.();
    speakerPump = null;
    screenVideo.pause();
    screenVideo.srcObject = null;
    if (speakerVideo) {
      speakerVideo.pause();
      speakerVideo.srcObject = null;
    }
    throw err;
  }

  const stopLoop = startFrameLoop(draw, { fps: 30 });
  draw();

  const stop = () => {
    stopped = true;
    stopLoop();
    speakerPump?.stop?.();
    speakerPump = null;
    capture?.stop?.();
    capture = null;
    screenVideo.pause();
    screenVideo.srcObject = null;
    if (speakerVideo) {
      speakerVideo.pause();
      speakerVideo.srcObject = null;
    }
  };

  return { stream: capture.stream, stop };
}
